import { ReactNode } from 'react'

import { TokenDisplay } from '..'
import { TruncateWithTooltip } from '~/components/TruncateWithTooltip'
import { Tooltip } from '~/components/component-library'
import { AddressDisplay } from '~/components/component-library/AddressDisplay'
import { Skeleton } from '~/components/component-library/Skeleton'
import { TokenBalance } from '~/components/component-library/TokenBalance'
import { TransactionFeatureTags } from '~/components/component-library/TransactionTitle/TransactionFeatureTags'
import { SupportedChainId } from '~/constants/chains'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import {
  AssetType,
  BackendTransaction,
  IAddress,
  OrgAssetTransfer,
} from '~/types'
import {
  AmountChunk,
  RecipientsChunk,
  TokenSymbolChunk,
} from '~/utils/smartAccounts/titleChunks'
import { cn } from '~/utils/tailwind-utils'
import { getTokenRepresentation } from '~/utils/tokenRepresentation'

type BaseProps<Features extends boolean = false> = {
  transaction: Features extends true
    ? BackendTransaction
    : Pick<BackendTransaction | OrgAssetTransfer, 'titleChunks' | 'chainId'>
  className?: string
  isSmall?: boolean
  showNetworkIcon?: boolean
  /**
   * Shows icons for transaction features such as privacy.
   */
  showFeatures?: Features
  /**
   * Truncate overflowing content and show a tooltip on hover
   */
  tooltipOnTruncate?: boolean
}

type StyledProps<Features extends boolean = false> = BaseProps<Features> & {
  unstyled?: false
  textWrap: boolean
}

type UnstyledProps<Features extends boolean = false> = BaseProps<Features> & {
  unstyled: true
  textWrap?: never
}

type TransactionTitleProps<Features extends boolean = false> =
  StyledProps<Features> | UnstyledProps<Features>

export const TransactionTitle = <Features extends boolean = false>({
  transaction,
  unstyled = false,
  className,
  textWrap,
  isSmall = false,
  showNetworkIcon = false,
  showFeatures,
  tooltipOnTruncate = false,
}: TransactionTitleProps<Features>) => {
  const hideAmount = true // leaving this for now in case we reverse our decision to hide amounts
  const titleChunks = transaction.titleChunks

  if (!titleChunks || titleChunks.length === 0) {
    return <Skeleton className="h-6 w-full" />
  }

  const titleElements: ReactNode[] = titleChunks.map((chunk, index) => {
    switch (chunk.kind) {
      case 'text':
        return <span key={index}>{chunk.value}</span>
      case 'address':
        return (
          <AddressDisplay
            key={index}
            address={chunk.address as IAddress}
            layout="inline-text"
            hidePopover={unstyled}
          />
        )
      case 'recipients':
        if (unstyled)
          return <span key={index}>{chunk.recipients.length} recipients</span>
        return (
          <MultiRecipientChunkComponent
            key={index}
            chunk={chunk}
            chainId={transaction.chainId}
            showNetworkIcon={showNetworkIcon}
          />
        )
      case 'tokenSymbol':
        return <TokenSymbolChunkComponent key={index} chunk={chunk} />
      case 'amount':
        return hideAmount ? (
          chunk.assetType === 'ERC1155' || chunk.assetType === 'ERC721' ? (
            <NFTChunkComponent key={index} chunk={chunk} />
          ) : (
            <TokenSymbolChunkComponent
              key={index}
              chunk={{
                kind: 'tokenSymbol',
                chainId: chunk.chainId,
                address: chunk.tokenAddress,
                assetType: chunk.assetType as AssetType,
                symbol: chunk.tokenSymbol,
                tokenId: chunk.nftTokenId,
                vaultTokenAddress: chunk.vaultTokenAddress,
                vaultTokenSymbol: chunk.vaultTokenSymbol,
              }}
            />
          )
        ) : (
          <AmountChunkComponent
            key={index}
            chunk={chunk}
            showNetworkIcon={showNetworkIcon && !unstyled}
            isSmall={isSmall && !unstyled}
          />
        )
      default:
        // Show nothing if an unknown chunk is reached. It may be a new one in the server and we may be in an old browser version.
        return null
    }
  })

  if (unstyled) {
    return <span>{titleElements}</span>
  }

  const title = (
    <div
      className={cn(className, 'w-full', {
        'block overflow-hidden text-ellipsis whitespace-nowrap': !textWrap,
      })}
    >
      {tooltipOnTruncate ? (
        <TruncateWithTooltip content={titleElements}>
          <>{titleElements}</>
        </TruncateWithTooltip>
      ) : (
        titleElements
      )}
    </div>
  )

  return (
    <div className="flex min-w-0 items-center gap-2">
      {title}
      {showFeatures && (
        <TransactionFeatureTags
          transaction={transaction as BackendTransaction}
        />
      )}
    </div>
  )
}

const MultiRecipientChunkComponent = ({
  chunk,
  chainId,
  showNetworkIcon,
}: {
  chunk: RecipientsChunk
  chainId: SupportedChainId
  showNetworkIcon?: boolean
}) => {
  return (
    <Tooltip
      content={
        <div className="space-y-2 text-sm">
          {chunk.recipients.map((recipient) => (
            <div
              key={recipient.address}
              className="flex items-center justify-between space-x-3"
            >
              <AddressDisplay
                address={recipient.address as IAddress}
                chainId={chainId}
                layout="inline-text"
              />
              <div>
                <AmountChunkComponent
                  chunk={{
                    kind: 'amount',
                    tokenAddress: recipient.tokenAddress,
                    chainId,
                    tokenAmount: recipient.amount,
                    assetType: recipient.assetType as AssetType,
                  }}
                  showNetworkIcon={showNetworkIcon}
                />
              </div>
            </div>
          ))}
        </div>
      }
      className="inline-flex"
    >
      <>{chunk.recipients.length} recipients</>
    </Tooltip>
  )
}

const TokenSymbolChunkComponent = ({ chunk }: { chunk: TokenSymbolChunk }) => (
  <TokenDisplay
    address={chunk.address as IAddress}
    chainId={chunk.chainId}
    layout="inline-text"
  />
)

const AmountChunkComponent = ({
  chunk,
  showNetworkIcon,
  isSmall,
}: {
  chunk: AmountChunk
  showNetworkIcon?: boolean
  isSmall?: boolean
}) => {
  if (chunk.assetType === 'ERC1155' || chunk.assetType === 'ERC721') {
    return <NFTChunkComponent chunk={chunk} />
  }

  return (
    <TokenBalance
      address={chunk.tokenAddress as IAddress}
      chainId={chunk.chainId}
      tokenAmount={BigInt(chunk.tokenAmount)}
      usdValue={
        chunk.historicalUsdValue ? Number(chunk.historicalUsdValue) : undefined
      }
      layout="inline"
      size={isSmall ? 'small' : 'base'}
      direction="neutral"
      showNetworkIcon={showNetworkIcon}
    />
  )
}

const NFTChunkComponent = ({ chunk }: { chunk: AmountChunk }) => {
  const tokenMetadata = useTokenMetadata({
    chainId: chunk.chainId,
    address: chunk.tokenAddress as IAddress,
    tokenId: chunk.nftTokenId,
    defaultSymbolToShortenAddress: true,
  })

  const tokenRepresentation = getTokenRepresentation(
    {
      assetType: chunk.assetType as AssetType,
      nftAddress: chunk.tokenAddress,
      nftTokenId: chunk.nftTokenId,
      nftName: chunk.nftName,
      nftSymbol: chunk.tokenSymbol,
    },
    {
      metadata: tokenMetadata,
      amount: BigInt(chunk.tokenAmount).toString(),
      showAmount: true,
    },
  )

  return <>{tokenRepresentation}</>
}
